import React from 'react';
import classNames from 'classnames';
import { titleize } from 'inflection';

import ClosingDoor from 'buyside/components/presentation/ClosingDoor/closingDoor';
import Link from 'buyside/components/presentation/Link/link';
import ResearchPositionHeader from 'buyside/components/presentation/ResearchItem/ResearchItemHeader/ResearchPositionHeader/researchPositionHeader';
import {
  IdeaResearchItemProps,
  ResearchTypes,
  SharedResearchItemProps,
  WhitePaperResearchItemProps
} from 'buyside/components/presentation/ResearchItem/researchItem';

import { formatDate } from 'buyside/util/dateHelper';
import { contestUrl, weeklyTopIdeasUrl, whitePaperShowUrl } from 'buyside/util/urlHelper';

import styles from './researchItemHeaderStyles.scss';

type SharedResearchItemHeaderProps = Pick<SharedResearchItemProps, 'narrow' | 'postedAt'>;

type IdeaResearchItemHeaderProps = Pick<
  IdeaResearchItemProps,
  | 'id'
  | 'canCurrentUserViewIdeaFilterDetails'
  | 'closedAt'
  | 'awardWinningContestIdeas'
  | 'expectedReturn'
  | 'positions'
  | 'submittedAt'
  | 'totalReturn'
  | 'updatedAt'
  | 'weeklyTopIdea'
> &
  SharedResearchItemHeaderProps & {
    researchType: ResearchTypes.IDEA;
  };

type WhitePaperResearchItemHeaderProps = Pick<
  WhitePaperResearchItemProps,
  'id' | 'submittedAt' | 'title' | 'updatedAt'
> &
  SharedResearchItemHeaderProps & {
    researchType: ResearchTypes.WHITE_PAPER;
    title?: string;
  };

const contestMap: { [key: string]: string } = {
  '4winner': '/images/pro/contest_gold.png',
  '3finalist': '/images/pro/contest_silver.png',
  '2runner_up': '/images/pro/contest_bronze.png',
  '1honoree': '/images/pro/contest_honoree.png'
};

const ResearchItemHeader: React.FC<
  IdeaResearchItemHeaderProps | WhitePaperResearchItemHeaderProps
> = props => {
  const { id, postedAt, submittedAt, updatedAt, researchType, narrow } = props;

  const isWhitePaper = researchType === ResearchTypes.WHITE_PAPER;

  return (
    <section className={classNames(styles.researchHeader, { [styles.narrow]: narrow })}>
      <div className={styles.researchHeaderTitle}>
        {isWhitePaper ? (
          <Link to={whitePaperShowUrl(id)}>{titleize(props.title)}</Link>
        ) : (
          props.positions.map(position => (
            <ResearchPositionHeader
              key={position.id}
              canCurrentUserViewIdeaFilterDetails={props.canCurrentUserViewIdeaFilterDetails}
              closedAt={props.closedAt}
              expectedReturn={props.expectedReturn}
              ideaId={id}
              isPair={(props.positions.length || 0) > 1}
              narrow={narrow}
              position={position}
              totalReturn={props.totalReturn}
            />
          ))
        )}
      </div>
      <div>
        <div className={styles.researchHeaderDates}>
          {postedAt ? (
            <span>{postedAt} ago</span>
          ) : (
            <>
              <span>
                <strong>Submitted</strong>
                {formatDate(submittedAt)}
              </span>
              <span>
                <strong>Updated</strong>
                {formatDate(updatedAt)}
              </span>
              {!isWhitePaper && props.closedAt ? (
                <>
                  <span>
                    <strong>Closed</strong>
                    {formatDate(props.closedAt)}
                  </span>
                  <ClosingDoor />
                </>
              ) : null}
            </>
          )}
        </div>
        {isWhitePaper ? null : (
          <div className={styles.researchTrophyIcons}>
            {props.awardWinningContestIdeas?.map(contestIdea =>
              contestIdea?.award ? (
                <Link to={contestUrl(contestIdea.contestId)} key={contestIdea.id}>
                  <img
                    alt="placed in contest"
                    className={styles.researchTrophyIcon}
                    src={contestMap[contestIdea.award]}
                  />
                </Link>
              ) : null
            )}
            {props.weeklyTopIdea ? (
              <Link to={weeklyTopIdeasUrl()}>
                <img
                  alt="weekly top idea winner"
                  className={styles.researchTrophyIcon}
                  src="/images/pro/wti_badge.png"
                />
              </Link>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
};

export default ResearchItemHeader;
